import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

export interface ChangelogEntry {
  id: number;
  date: string;
  /** Kod tagu: 'new' | 'fix' | 'improve' (etykiety przez i18n). */
  tag?: string;
  /** Wielojęzycznie: { pl, en, es }; string dopuszczony dla starych wpisów. */
  title: { [lang: string]: string } | string;
  text: { [lang: string]: string } | string;
}

/**
 * Dziennik zmian panelu. Treść = statyczny plik assets/changelog.json (dopisywany
 * przy każdej zmianie i wdrażany razem z nią). Wskaźnik "nowe zmiany" trzymamy
 * per użytkownik w localStorage - bez backendu i bazy: porównujemy najwyższe id
 * wpisu z ostatnio widzianym przez tego usera.
 *
 * Cache-busting `?t=` wymusza świeży plik przy każdym starcie aplikacji (assets
 * nie są hashowane przez Angular, więc bez tego przeglądarka trzymałaby stary).
 */
@Injectable({ providedIn: 'root' })
export class ChangelogService {
  private readonly entriesSubject = new BehaviorSubject<ChangelogEntry[]>([]);
  readonly entries$ = this.entriesSubject.asObservable();

  private readonly hasUnseenSubject = new BehaviorSubject<boolean>(false);
  readonly hasUnseen$ = this.hasUnseenSubject.asObservable();

  private loaded = false;

  constructor(private http: HttpClient) {}

  /** Woływane raz przy starcie (z menu) - ładuje wpisy i ustala wskaźnik. */
  init(): void {
    if (this.loaded) { return; }
    this.loaded = true;
    this.http
      .get<ChangelogEntry[]>('./assets/changelog.json?t=' + Date.now())
      .pipe(
        catchError(() => of([] as ChangelogEntry[])),
        tap(entries => {
          const sorted = (entries ?? []).slice().sort((a, b) => b.id - a.id);
          this.entriesSubject.next(sorted);
          this.hasUnseenSubject.next(this.computeUnseen(sorted));
        }),
      )
      .subscribe();
  }

  /** Oznacz wszystkie zmiany jako przeczytane przez bieżącego użytkownika. */
  markSeen(): void {
    const latest = this.latestId();
    if (latest != null) {
      localStorage.setItem(this.storageKey(), String(latest));
    }
    this.hasUnseenSubject.next(false);
  }

  private computeUnseen(entries: ChangelogEntry[]): boolean {
    const latest = entries.length ? entries[0].id : null;
    if (latest == null) { return false; }
    const seenRaw = localStorage.getItem(this.storageKey());
    // Brak zapisu = użytkownik nigdy nie otworzył dziennika -> pokaż kropkę raz
    // (odkrycie zakładki). Po pierwszym wejściu markSeen ustawi bazę.
    if (seenRaw == null) { return true; }
    return latest > Number(seenRaw);
  }

  private latestId(): number | null {
    const entries = this.entriesSubject.value;
    return entries.length ? entries[0].id : null;
  }

  private storageKey(): string {
    let userId = 'anon';
    try {
      const u = JSON.parse(localStorage.getItem('LoggedInUser') || '{}');
      if (u && u.id != null) { userId = String(u.id); }
    } catch { /* localStorage bez usera - klucz anon */ }
    return 'changelog:lastSeenId:' + userId;
  }
}
